/**
 * Board -> mini-game transition screen (docs/concept/board/06-minimal-transition.png, which supersedes the
 * styling of 05-minigame-transition.png; see both `.md` files beside them, and the "Minimal white card"
 * paragraph in docs/CURRENT-PRODUCT-DIRECTION.md). A white rounded card floats over a blurred, softened —
 * but still visible and recognisable — version of the board, which the caller supplies as `backdrop` (this
 * component only applies the blur; it never renders board content of its own). No nested boxes, no icon
 * backgrounds, no reveal infographic, no countdown rays.
 *
 * Every string that names a specific game ("Under Pressure", its goal/play/score copy, its format) is a prop.
 * This component invents none of it — there is no real mini-game yet (docs/WORLD-EXPANSION-BRIEF.md), and
 * `strings.transition` only holds structural chrome (labels like "Ready", "VS", "Starting together").
 *
 * Driven entirely by props: `stage` (from `useBoardTransitionStage`), `players`/`teams`/readiness (from
 * whoever assembles the roster — see `boardTransitionData.ts` for the real mount, `BoardTransitionDemo.tsx`
 * for the illustrative one), and `countdownValue`. It never owns the state machine itself.
 */
import type { CSSProperties, ReactNode } from 'react';
import { CHARACTER_SLOTS, type CharacterSlotId } from '@momentum/shared';
import { characterAssetUrl } from '../assets/index.ts';
import { strings } from '../shell/strings.ts';
import { BerryAvatar } from '../shell/BerryAvatar.tsx';
import './board-transition.css';

export type BoardTransitionUiStage = 'reveal' | 'briefing' | 'ready' | 'countdown';

export interface BoardTransitionPlayer {
  id: string;
  name: string;
  teamId: string | null;
  characterSlot: CharacterSlotId | null;
  /** Whether this player has pressed Ready. The sole gate for the automatic countdown. */
  ready: boolean;
  disconnected?: boolean;
  /** Server readiness is distinct from asset loading. */
  waitingLabel?: string;
}

export interface BoardTransitionTeam {
  id: string;
  label: string;
  /** CSS colour for the label text; falls back to the first player's character colour, then plain ink. */
  accentColor?: string;
}

export interface BoardTransitionBriefing {
  goal: string;
  play: string;
  score: string;
}

export interface BoardTransitionHud {
  title: string;
  crystalsCollected: number;
  crystalsTotal: number;
  /** Omitted (or left undefined) renders the map control disabled — honest, since the board is non-interactive
   * behind the blur during this transition anyway. */
  onOpenMap?: () => void;
}

export interface BoardTransitionProps {
  stage: BoardTransitionUiStage;
  gameTitle: string;
  /** e.g. "2 vs 2 · About 2 minutes" — composed by the caller, never invented here. */
  format: string;
  players: BoardTransitionPlayer[];
  teams?: BoardTransitionTeam[];
  /** Goal/Play/Score rows for the briefing stage. Omit to skip that section entirely. */
  briefing?: BoardTransitionBriefing | null;
  selfParticipantId?: string | null;
  countdownValue?: number | null;
  countdownLabel?: string;
  /** Briefing stage only. Omitted/undefined hides the Ready button (nothing to press yet). */
  onReady?: () => void;
  readyDisabled?: boolean;
  /** Explicit disconnect-recovery text (05-minigame-transition.md: "must remain explicit", never a silent hang). */
  disconnectNotice?: string | null;
  hud?: BoardTransitionHud | null;
  /** Whatever should render, blurred, behind the card — normally the still-live board. */
  backdrop?: ReactNode;
  reducedMotion?: boolean;
}

export function BoardTransition({
  stage,
  gameTitle,
  format,
  players,
  teams = [],
  briefing = null,
  selfParticipantId = null,
  countdownValue = null,
  countdownLabel,
  onReady,
  readyDisabled = false,
  disconnectNotice = null,
  hud = null,
  backdrop = null,
  reducedMotion = false,
}: BoardTransitionProps) {
  return (
    <div className="board-transition">
      {hud ? <TransitionHud hud={hud} /> : null}
      <div className="board-transition__backdrop" aria-hidden="true">
        {backdrop}
      </div>
      <div className="board-transition__frame">
        <div
          className="board-transition__card"
          role="group"
          aria-label={`${strings.transition.regionLabel}: ${gameTitle}`}
          aria-live={stage === 'countdown' ? 'polite' : undefined}
        >
          {disconnectNotice ? (
            <p className="board-transition__notice" role="alert">
              {disconnectNotice}
            </p>
          ) : null}

          {stage === 'reveal' ? <RevealStage gameTitle={gameTitle} format={format} teams={teams} players={players} /> : null}

          {stage === 'briefing' ? (
            <BriefingStage
              gameTitle={gameTitle}
              format={format}
              briefing={briefing}
              teams={teams}
              players={players}
              selfParticipantId={selfParticipantId}
              onReady={onReady}
              readyDisabled={readyDisabled}
            />
          ) : null}

          {stage === 'ready' ? (
            <ReadyStage
              gameTitle={gameTitle}
              format={format}
              teams={teams}
              players={players}
              selfParticipantId={selfParticipantId}
              reducedMotion={reducedMotion}
            />
          ) : null}

          {stage === 'countdown' ? (
            <CountdownStage
              gameTitle={gameTitle}
              format={format}
              teams={teams}
              players={players}
              countdownValue={countdownValue}
              countdownLabel={countdownLabel}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function RevealStage({
  gameTitle,
  format,
  teams,
  players,
}: {
  gameTitle: string;
  format: string;
  teams: BoardTransitionTeam[];
  players: BoardTransitionPlayer[];
}) {
  return (
    <div className="board-transition__stage">
      <p className="board-transition__eyebrow">{strings.transition.nextUp}</p>
      <h2 className="board-transition__title">{gameTitle}</h2>
      <p className="board-transition__format">{format}</p>
      <PairsWithVs teams={teams} players={players} layout="stacked" />
    </div>
  );
}

function BriefingStage({
  gameTitle,
  format,
  briefing,
  teams,
  players,
  selfParticipantId,
  onReady,
  readyDisabled,
}: {
  gameTitle: string;
  format: string;
  briefing: BoardTransitionBriefing | null;
  teams: BoardTransitionTeam[];
  players: BoardTransitionPlayer[];
  selfParticipantId: string | null;
  onReady?: () => void;
  readyDisabled: boolean;
}) {
  return (
    <div className="board-transition__stage">
      <h2 className="board-transition__title">{gameTitle}</h2>
      <p className="board-transition__format">{format}</p>

      {briefing ? (
        <ul className="board-transition__briefing-list" role="list">
          <li>
            <GoalIcon />
            <span>
              <strong>{strings.transition.goal}</strong>
              <p>{briefing.goal}</p>
            </span>
          </li>
          <li>
            <PlayIcon />
            <span>
              <strong>{strings.transition.play}</strong>
              <p>{briefing.play}</p>
            </span>
          </li>
          <li>
            <ScoreIcon />
            <span>
              <strong>{strings.transition.score}</strong>
              <p>{briefing.score}</p>
            </span>
          </li>
        </ul>
      ) : null}

      <TeamRows teams={teams} players={players} showReadiness={false} selfParticipantId={selfParticipantId} />

      {onReady ? (
        <button
          type="button"
          className="button button-primary board-transition__ready-button"
          onClick={onReady}
          disabled={readyDisabled}
        >
          {readyDisabled ? strings.transition.readyPending : strings.transition.ready}
        </button>
      ) : null}
    </div>
  );
}

function ReadyStage({
  gameTitle,
  format,
  teams,
  players,
  selfParticipantId,
  reducedMotion,
}: {
  gameTitle: string;
  format: string;
  teams: BoardTransitionTeam[];
  players: BoardTransitionPlayer[];
  selfParticipantId: string | null;
  reducedMotion: boolean;
}) {
  const total = players.length;
  const readyCount = players.filter((p) => p.ready).length;
  const selfReady = players.find((p) => p.id === selfParticipantId)?.ready ?? false;

  return (
    <div className="board-transition__stage">
      <h2 className="board-transition__title">{gameTitle}</h2>
      <p className="board-transition__format">{format}</p>

      <TeamRows
        teams={teams}
        players={players}
        showReadiness
        selfParticipantId={selfParticipantId}
        reducedMotion={reducedMotion}
      />

      <hr className="board-transition__divider" />

      <p className="board-transition__ready-count" role="status" aria-live="polite">
        {strings.transition.readyCount(readyCount, total)}
      </p>

      {selfReady ? (
        <p className="board-transition__self-ready">
          <CheckIcon />
          {strings.transition.youAreReady}
        </p>
      ) : null}
    </div>
  );
}

function CountdownStage({
  gameTitle,
  format,
  teams,
  players,
  countdownValue,
  countdownLabel,
}: {
  gameTitle: string;
  format: string;
  teams: BoardTransitionTeam[];
  players: BoardTransitionPlayer[];
  countdownValue: number | null;
  countdownLabel?: string;
}) {
  const announcement =
    countdownValue !== null && countdownValue > 0 ? strings.transition.startingIn(countdownValue) : strings.transition.startingNow;

  return (
    <div className="board-transition__stage">
      <h2 className="board-transition__title">{gameTitle}</h2>
      <p className="board-transition__format">{format}</p>
      <PairsWithVs teams={teams} players={players} layout="inline" />
      <p className="board-transition__countdown-number" aria-hidden="true">
        {countdownValue ?? ''}
      </p>
      <p className="sr-only" aria-live="assertive">
        {announcement}
      </p>
      <p className="board-transition__countdown-label">{countdownLabel ?? strings.transition.startingTogether}</p>
    </div>
  );
}

function PairsWithVs({
  teams,
  players,
  layout,
}: {
  teams: BoardTransitionTeam[];
  players: BoardTransitionPlayer[];
  layout: 'stacked' | 'inline';
}) {
  if (teams.length !== 2) {
    return (
      <ul className={`board-transition__pairs board-transition__pairs--${layout}`} role="list">
        {players.map((player) => (
          <li key={player.id}>
            <PlayerAvatar player={player} showReadiness={false} />
          </li>
        ))}
      </ul>
    );
  }

  const [teamA, teamB] = teams;
  const groupA = players.filter((p) => p.teamId === teamA!.id);
  const groupB = players.filter((p) => p.teamId === teamB!.id);

  return (
    <div className={`board-transition__pairs board-transition__pairs--${layout}`}>
      <ul className="board-transition__pair" role="list" aria-label={teamA!.label}>
        {groupA.map((player) => (
          <li key={player.id}>
            <PlayerAvatar player={player} showReadiness={false} />
          </li>
        ))}
      </ul>
      <span className="board-transition__vs" aria-hidden="true">
        {strings.transition.vs}
      </span>
      <ul className="board-transition__pair" role="list" aria-label={teamB!.label}>
        {groupB.map((player) => (
          <li key={player.id}>
            <PlayerAvatar player={player} showReadiness={false} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function TeamRows({
  teams,
  players,
  showReadiness,
  selfParticipantId,
  reducedMotion,
}: {
  teams: BoardTransitionTeam[];
  players: BoardTransitionPlayer[];
  showReadiness: boolean;
  selfParticipantId: string | null;
  reducedMotion?: boolean;
}) {
  const groups =
    teams.length > 0 ? teams.map((team) => ({ team, players: players.filter((p) => p.teamId === team.id) })) : [{ team: null, players }];

  return (
    <div className="board-transition__teams">
      {groups.map(({ team, players: group }, index) => (
        <div className="board-transition__team-row" key={team?.id ?? index}>
          {team ? (
            <p className="board-transition__team-label" style={{ color: team.accentColor ?? teamAccentColor(group) }}>
              {team.label}
            </p>
          ) : null}
          <ul className="board-transition__pair" role="list" aria-label={team?.label}>
            {group.map((player) => (
              <li key={player.id}>
                <PlayerAvatar
                  player={player}
                  self={player.id === selfParticipantId}
                  showReadiness={showReadiness}
                  reducedMotion={reducedMotion}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function teamAccentColor(players: BoardTransitionPlayer[]): string | undefined {
  const slot = players.find((p) => p.characterSlot)?.characterSlot;
  return slot ? `var(--${CHARACTER_SLOTS[slot].colorToken})` : undefined;
}

function PlayerAvatar({
  player,
  self = false,
  showReadiness,
  reducedMotion,
}: {
  player: BoardTransitionPlayer;
  self?: boolean;
  showReadiness: boolean;
  reducedMotion?: boolean;
}) {
  const appearance = player.characterSlot ? CHARACTER_SLOTS[player.characterSlot] : null;
  const colorVar = appearance ? `var(--${appearance.colorToken})` : 'var(--ink-muted)';
  const displayName = self ? strings.transition.you : player.name;

  return (
    <div className="board-transition__player">
      <span className="board-transition__avatar" style={{ borderColor: colorVar } as CSSProperties}>
        <BerryAvatar slot={player.characterSlot} size={30} />
        {showReadiness ? (
          <span className={`board-transition__ready-badge ${player.ready ? 'is-ready' : 'is-loading'}`} aria-hidden="true">
            {player.ready ? <CheckIcon /> : <SpinnerIcon reducedMotion={reducedMotion} />}
          </span>
        ) : null}
      </span>
      <span className="board-transition__player-name">{displayName}</span>
      {showReadiness && !player.ready ? (
        <span className="board-transition__player-loading">{player.waitingLabel ?? strings.transition.playerLoading(displayName)}</span>
      ) : showReadiness ? (
        <span className="sr-only">{strings.transition.playerReady(displayName)}</span>
      ) : null}
      {player.disconnected ? <span className="sr-only">{strings.transition.playerDisconnected(displayName)}</span> : null}
    </div>
  );
}

function TransitionHud({ hud }: { hud: BoardTransitionHud }) {
  const pips = Array.from({ length: Math.max(0, hud.crystalsTotal) }, (_, i) => i < hud.crystalsCollected);
  return (
    <div className="board-transition__hud">
      <span className="board-transition__hud-title">{hud.title}</span>
      {hud.crystalsTotal > 0 ? (
        <span className="board-transition__hud-crystals" aria-label={strings.transition.crystals(hud.crystalsCollected, hud.crystalsTotal)}>
          {pips.map((filled, i) => (
            <span key={i} aria-hidden="true" className={`board-transition__hud-pip${filled ? ' is-filled' : ''}`} />
          ))}
          <span aria-hidden="true" className="board-transition__hud-count">
            {hud.crystalsCollected} / {hud.crystalsTotal}
          </span>
        </span>
      ) : null}
      <button
        type="button"
        className="board-transition__hud-map"
        onClick={hud.onOpenMap}
        disabled={!hud.onOpenMap}
        aria-label={hud.onOpenMap ? strings.transition.mapButton : strings.transition.mapUnavailable}
      >
        <MapIcon />
      </button>
    </div>
  );
}

/* --- Small thin-stroke icons only: no fills, no icon backgrounds (06-minimal-transition.md). --- */

function GoalIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="8.5" cy="9" r="2.6" />
      <circle cx="16" cy="10.5" r="2.1" />
      <path strokeLinecap="round" d="M3.5 19c.4-3 2.4-5 5-5s4.6 2 5 5M14.3 19c.3-2.1 1.6-3.7 3.4-4.2" />
    </svg>
  );
}

function ScoreIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <path d="M12 3.5l2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6-4.4-4.2 6-.8z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12.5l5 5L20 6" />
    </svg>
  );
}

function SpinnerIcon({ reducedMotion }: { reducedMotion?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      className={reducedMotion ? undefined : 'board-transition__spin'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    >
      <path d="M12 3a9 9 0 1 0 9 9" />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <path d="M9 4.5L4 6.3v13.2l5-1.8 6 1.8 5-1.8V4.5l-5 1.8-6-1.8z" />
      <path d="M9 4.5v13.2M15 6.3v13.2" />
    </svg>
  );
}
